import { AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, useField } from "formik";
import { InputControl } from "formik-chakra-ui";
import React from "react";
import { FormikSearchSelect } from "src/components/Inputs/SearchSelect";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  createDataPoint,
  type EligibilityDataPoint,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  generateKey,
  isUserGeneratedKey,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import { validateWithZod } from "src/services/formValidations";
import * as RD from "src/types/remoteData";
import { z } from "zod";

type DataPointFormValues = {
  name: string;
  key: string;
};

const duplicateKeyMessage = "This key already exists";

function dataPointFormSchema(existingKeys: readonly string[]) {
  const keys = new Set(existingKeys.map((key) => key.trim()));
  return z.object({
    name: z.string().trim().min(1, "This field is required"),
    key: z
      .string()
      .trim()
      .min(1, "This field is required")
      .refine((key) => !keys.has(key), duplicateKeyMessage),
  });
}

const dataPointCopy = {
  Address: {
    empty: "No address data points for this enrollment period",
    create: "New address data point",
  },
  Date: {
    empty: "No date data points for this enrollment period",
    create: "New date data point",
  },
  SingleSelect: {
    empty: "No single select data points for this enrollment period",
    create: "New single select data point",
  },
} as const;

export type SelectableDataPointType = keyof typeof dataPointCopy;

export function DataPointSelect({
  name,
  enrollmentPeriodId,
  dataPoints,
  dataPointType,
}: {
  name: string;
  enrollmentPeriodId: string;
  dataPoints: RD.RemoteData<Error, EligibilityDataPoint[]>;
  dataPointType: SelectableDataPointType;
}) {
  const [, , helpers] = useField<string>(name);
  const createModal = useDisclosure();
  const [formKey, setFormKey] = React.useState(0);
  const [created, setCreated] = React.useState<EligibilityDataPoint | null>(
    null
  );
  const items = React.useMemo(() => {
    const points = (dataPoints.toNullable() ?? []).filter(
      (point) => point.type === dataPointType
    );
    const withCreated =
      created &&
      created.type === dataPointType &&
      !points.some((point) => point.id === created.id)
        ? [...points, created]
        : points;
    return withCreated.map((point) => ({
      key: point.id,
      label: formatDataPoint(point),
    }));
  }, [created, dataPointType, dataPoints]);
  const isLoading = !dataPoints.hasError() && !dataPoints.hasData();

  const openCreate = () => {
    setFormKey((key) => key + 1);
    createModal.onOpen();
  };

  return (
    <>
      <FormikSearchSelect
        name={name}
        label="Data point"
        items={items}
        placeholder={isLoading ? "Loading data points" : "Select a data point"}
        disabled={isLoading}
        variant="unlocked"
        hideClearButton
        formControlProps={{ id: name }}
        renderEmptyItems={() => (
          <Text fontSize="sm" fontStyle="italic" color="gray.500" px={4} py={2}>
            {items.length === 0
              ? dataPointCopy[dataPointType].empty
              : "No matching data points"}
          </Text>
        )}
        footer={
          <Box p={3} pb={2}>
            <Button
              type="button"
              variant="outline"
              colorScheme="gray"
              fontWeight="normal"
              size="sm"
              leftIcon={<AddIcon boxSize={2.5} />}
              onClick={openCreate}
            >
              {dataPointCopy[dataPointType].create}
            </Button>
          </Box>
        }
      />
      <Modal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        isCentered
      >
        <ModalOverlay />
        <NewDataPointForm
          key={formKey}
          enrollmentPeriodId={enrollmentPeriodId}
          dataPointType={dataPointType}
          existingKeys={existingDataPointKeys(
            dataPoints.toNullable() ?? [],
            created
          )}
          title={dataPointCopy[dataPointType].create}
          onCancel={createModal.onClose}
          onCreated={(point) => {
            setCreated(point);
            void helpers.setTouched(true);
            void helpers.setValue(point.id);
            createModal.onClose();
          }}
        />
      </Modal>
    </>
  );
}

function formatDataPoint(point: Pick<EligibilityDataPoint, "name" | "key">) {
  return `${point.name} (${point.key})`;
}

function existingDataPointKeys(
  points: EligibilityDataPoint[],
  created: EligibilityDataPoint | null
) {
  const keys = points.map((point) => point.key);
  if (created && !points.some((point) => point.id === created.id)) {
    keys.push(created.key);
  }
  return keys;
}

function NewDataPointForm({
  enrollmentPeriodId,
  dataPointType,
  existingKeys,
  title,
  onCancel,
  onCreated,
}: {
  enrollmentPeriodId: string;
  dataPointType: SelectableDataPointType;
  existingKeys: readonly string[];
  title: string;
  onCancel: () => void;
  onCreated: (dataPoint: EligibilityDataPoint) => void;
}) {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSubmit = async (values: DataPointFormValues) => {
    setIsSaving(true);
    try {
      const result = await createDataPoint({
        throwOnError: true,
        path: { enrollmentPeriodId },
        body: {
          name: values.name.trim(),
          key: values.key.trim(),
          type: dataPointType,
        },
      });
      toast({
        id: "create-data-point",
        title: "Data point created",
      });
      await queryClient.invalidateQueries({
        queryKey: [FSQ.useListDataPointsKey],
      });
      onCreated(result.data.data);
    } catch (err) {
      console.error(err);
      toast.error({
        id: "create-data-point",
        title: "Failed to create data point",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Formik<DataPointFormValues>
      initialValues={{ name: "", key: "" }}
      onSubmit={handleSubmit}
      validate={(values) =>
        validateWithZod(dataPointFormSchema(existingKeys))(values)
      }
      validateOnBlur={false}
    >
      {(formik) => (
        <ModalContent as={Form} noValidate>
          <ModalHeader>{title}</ModalHeader>
          <ModalBody>
            <InputControl
              name="name"
              label="Name"
              marginBottom={4}
              inputProps={{
                autoFocus: true,
                onChange: (event) => {
                  const nextName = event.target.value;
                  const previousName = formik.values.name;
                  const previousKey = formik.values.key;
                  void formik.setFieldValue("name", nextName);
                  if (!isUserGeneratedKey(previousKey, previousName)) {
                    void formik.setFieldValue("key", generateKey(nextName));
                  }
                },
              }}
            />
            <InputControl
              name="key"
              label="Key"
              marginBottom={0}
              {...(formik.errors.key === duplicateKeyMessage
                ? { isInvalid: true }
                : {})}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              type="button"
              variant="ghost"
              colorScheme="gray"
              onClick={onCancel}
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSaving}
              loadingText="Creating data point"
              isDisabled={!formik.isValid || !formik.dirty}
            >
              Create data point
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
    </Formik>
  );
}
